import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { useShouldPanelBeVisible } from '~/eds/src/components/Panel/PanelContext';
import { Text } from '~/eds/src/components/Text/Text';
import {
  BatchAddGroupNodeItem,
  BatchAddGroupNodePayload,
} from '~/redux/api/methods/group-nodes/types';

import { AddItemsFooter, AddToGroupStepType } from './AddItemsFooter';
import { SelectItemsStep } from './SelectItemsStep';
import { SelectParentStep, SelectParentStepProps } from './SelectParentStep';

const messages = defineMessages({
  panelTitle: {
    id: 'AddItemsPanel.panelTitle',
    defaultMessage: 'Add items to group',
    description: 'Title for the add items panel',
  },
  description: {
    id: 'AddItemsPanel.description',
    defaultMessage:
      'Add documents and in-progress tickets to this group. Note: tickets that are completed are searchable only by its document name.',
    description: 'Description for the add items panel',
  },
});

export enum Step {
  SELECT_ITEMS = 'select-items',
  SELECT_PARENT = 'select-parent',
}

export const AddItemsPanelId = 'add-items';

export interface AddItemsPanelProps {
  groupId: string;
  rootNodeId?: string;
  onClose: () => void;
  onItemsAdded: (payload: BatchAddGroupNodePayload) => void;
  onLoadAllNodes?: SelectParentStepProps['onLoadAllNodes'];
}

export const AddItemsPanel = ({
  rootNodeId,
  onClose,
  onItemsAdded,
  onLoadAllNodes,
}: AddItemsPanelProps) => {
  const { formatMessage } = useIntl();
  const [step, setStep] = useState<AddToGroupStepType>('select-items');
  const [selectedItems, setSelectedItems] = useState<BatchAddGroupNodeItem[]>(
    [],
  );
  const [selectedParentNode, setSelectedParentNode] = useState<string | null>(
    null,
  );

  const selectedIds = useMemo(
    () => selectedItems.map((item) => item.resourceId),
    [selectedItems],
  );

  const handleClose = () => {
    setStep(Step.SELECT_ITEMS);
    setSelectedParentNode(null);
    setSelectedItems([]);
    onClose();
  };

  const handleNext = () => {
    if (step === Step.SELECT_ITEMS) {
      setStep(Step.SELECT_PARENT);
    } else {
      onItemsAdded?.({
        parentNodeId: selectedParentNode ?? rootNodeId,
        items: selectedItems,
      });
      handleClose();
    }
  };

  const handleBack = () => {
    if (step === Step.SELECT_PARENT) {
      setStep(Step.SELECT_ITEMS);
    } else {
      handleClose();
    }
  };

  const show = useShouldPanelBeVisible(AddItemsPanelId);

  if (!show) {
    return null;
  }

  return (
    <Panel
      animate
      mode="floating"
      position="right"
      title={formatMessage(messages.panelTitle)}
      width="l"
      hidden={{
        isHidden: false,
        onHide: handleClose,
      }}
      footer={{
        children: (
          <AddItemsFooter
            selectedIds={selectedIds}
            step={step}
            onNext={handleNext}
            onBack={handleBack}
          />
        ),
      }}
    >
      <Layout
        direction="column"
        style={{
          height: '100%',
          justifyContent: 'stretch',
          overflow: 'hidden',
          gap: '16px',
        }}
      >
        <Text color="text.secondary">
          {formatMessage(messages.description)}
        </Text>

        <SelectItemsStep
          isVisible={step === Step.SELECT_ITEMS}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedItems}
        />

        <SelectParentStep
          isVisible={step === Step.SELECT_PARENT}
          selectedParentNode={selectedParentNode}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedParentNode}
          onLoadAllNodes={onLoadAllNodes}
        />
      </Layout>
    </Panel>
  );
};
